import React, { useMemo, useCallback } from 'react';

import {
  DisplayUserName,
  TOOL_IDS,
  useCurrentToolId,
} from '@adsk/alloy-provider';
import { Box, Flex } from '@adsk/alloy-react-box';
import { displayTime, VALID_DATE_FORMATS } from '@adsk/alloy-react-datetime';
import { FanIcon } from '@adsk/alloy-react-icon';
import Tooltip, { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import { useUsersByOxygenIdMap, useTemplateName } from '.';
import { useFormNameColumns } from './useFormNameColumns';
import InlineLoader from '../../../InlineLoader';
import { useFormsContext } from '../FormsProvider';
import FormStatus from '../FormStatus';
import { FEATURE_FLAGS, TABLE_COLUMN_ACCESSOR_KEY } from '../helpers/constants';
import { getFormDateString, getItemListString } from '../helpers/utils';
import { useTranslation } from '../i18n';
import { usePlangridFeatureFlag } from '../PlangridLaunchDarklyProvider';

export default function useTableColumns() {
  const { t, locale } = useTranslation();
  const { data: usersByOxygenIdMap, loading: isLoadingUsers } =
    useUsersByOxygenIdMap();
  const { templateCategories } = useFormsContext();
  const toolId = useCurrentToolId();
  const { value: enableRenameForms } = usePlangridFeatureFlag(
    FEATURE_FLAGS.RENAME_FORMS,
    false,
  );
  const { value: isRemovedMemberFormatOn } = usePlangridFeatureFlag(
    FEATURE_FLAGS.FORMS_REMOVED_MEMBER,
    false,
  );
  const { columns: formNameColumns, columnWidth: nameColumnWidth } =
    useFormNameColumns(enableRenameForms);

  const { getTemplateName } = useTemplateName();

  const TemplateNameCell = useCallback(
    ({ row }) => {
      const templateName = getTemplateName(row?.original);

      // The Assets icon should only render in the Assets tool
      if (templateCategories === undefined || toolId !== TOOL_IDS.ASSETS)
        return <OverflowTooltip>{templateName}</OverflowTooltip>;

      const categories = templateCategories.reduce(
        (acc, { name, templates }) => {
          if (templates.includes(row?.original?.type)) return [...acc, name];
          return acc;
        },
        [],
      );
      return (
        <Flex
          alignItems="center"
          width={categories.length > 0 ? '190px' : '230px'}
        >
          <OverflowTooltip>{templateName}</OverflowTooltip>
          {categories.length > 0 && (
            <Box paddingLeft="5px">
              <Tooltip
                placement="bottom"
                content={`${t('ADDED_TO')} ${getItemListString(categories)}`}
              >
                <FanIcon style={{ paddingTop: '5px' }} />
              </Tooltip>
            </Box>
          )}
        </Flex>
      );
    },
    [t, getTemplateName, templateCategories, toolId],
  );

  const renderUserName = useCallback(
    ({ getValue }) => {
      const oxygenId = getValue();
      if (isRemovedMemberFormatOn) {
        return (
          <OverflowTooltip>
            <DisplayUserName
              id={oxygenId}
              renderError={() => t('USER_NOT_FOUND')}
              renderLoader={() => <InlineLoader />}
            />
          </OverflowTooltip>
        );
      }
      if (isLoadingUsers) return <InlineLoader />;
      const user = usersByOxygenIdMap[oxygenId];
      if (user) return <OverflowTooltip>{user.name}</OverflowTooltip>;
      return t('USER_NOT_FOUND');
    },
    [t, isLoadingUsers, isRemovedMemberFormatOn, usersByOxygenIdMap],
  );

  const columns = useMemo(
    () => [
      ...formNameColumns,
      {
        header: enableRenameForms ? t('TEMPLATE_NAME') : t('NAME'),
        size: nameColumnWidth,
        minSize: nameColumnWidth,
        cell: TemplateNameCell,
      },
      {
        header: t('FORM_DATE'),
        accessorKey: TABLE_COLUMN_ACCESSOR_KEY.REPORT_DATE,
        size: 110,
        minSize: 110,
        cell: ({ getValue }) => getFormDateString(getValue(), locale),
      },

      {
        header: t('ID'),
        accessorKey: TABLE_COLUMN_ACCESSOR_KEY.REPORT_NUM,
        size: 70,
        minSize: 70,
        cell: ({ getValue }) => `#${getValue()}`,
      },

      {
        header: t('STATUS'),
        accessorKey: TABLE_COLUMN_ACCESSOR_KEY.STATUS,
        size: 120,
        minSize: 120,
        cell: ({ getValue }) => <FormStatus status={getValue()} />,
      },

      {
        header: t('CREATED_BY'),
        accessorKey: TABLE_COLUMN_ACCESSOR_KEY.CREATED_BY,
        size: 150,
        minSize: 150,
        cell: renderUserName,
      },

      {
        header: t('LAST_UPDATED'),
        accessorKey: TABLE_COLUMN_ACCESSOR_KEY.UPDATED_AT,
        size: 170,
        minSize: 170,
        cell: ({ getValue }) =>
          t('DATE_AT_TIME', {
            date: getFormDateString(getValue(), locale),
            time: displayTime(new Date(getValue()), {
              format: VALID_DATE_FORMATS.SHORT,
              locale,
            }),
          }),
      },

      {
        header: t('LAST_UPDATED_BY'),
        accessorKey: TABLE_COLUMN_ACCESSOR_KEY.UPDATED_BY,
        size: 150,
        minSize: 150,
        cell: renderUserName,
      },
    ],
    [
      formNameColumns,
      nameColumnWidth,
      enableRenameForms,
      t,
      TemplateNameCell,
      locale,
      renderUserName,
    ],
  );
  return columns;
}
